import { useEffect, useMemo, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { useDesignerStore } from "@/state/designerStore";
import { CameraRig } from "./CameraRig";
import { FixingMarkers } from "./FixingMarkers";
import { Interaction } from "./Interaction";
import { Lighting } from "./Lighting";
import { Workbench } from "./Workbench";
import { updateGlossUniforms, type BrushUniforms } from "./holdMaterial";

interface ViewportProps {
  engine: DesignerEngine;
  uniforms: BrushUniforms;
  onContextLost: () => void;
  cutRunner: React.MutableRefObject<(() => void) | null>;
}

/** Finish any meshing left over from a busy stroke, a slice at a time. */
function MesherPump({ engine }: { engine: DesignerEngine }): null {
  useFrame(() => {
    if (engine.mesher.pendingChunks > 0) engine.mesher.flush(6);
  });
  return null;
}

export function Viewport({
  engine,
  uniforms,
  onContextLost,
  cutRunner,
}: ViewportProps): React.ReactElement {
  const { width, height, depth } = engine.preset;
  const tool = useDesignerStore((s) => s.tool);
  const selectedFixingId = useDesignerStore((s) => s.selectedFixingId);
  const revision = useDesignerStore((s) => s.revision);
  const [ready, setReady] = useState(false);

  const diagonal = useMemo(() => Math.hypot(width, height, depth), [width, height, depth]);

  useEffect(() => {
    if (tool !== "fixings") uniforms.uBrushActive.value = 0;
  }, [tool, uniforms]);

  useEffect(() => {
    updateGlossUniforms(uniforms, engine.glossDabs);
  }, [engine, revision, uniforms]);

  return (
    <Canvas
      shadows="percentage"
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: false, preserveDrawingBuffer: false }}
      camera={{ fov: 38, near: 1, far: diagonal * 24 }}
      onCreated={({ gl, scene }) => {
        gl.setClearColor(new THREE.Color("#0b0d0c"));
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.02;
        // PCFSoftShadowMap is deprecated in current Three.js; the standard
        // filtered PCF mode keeps the studio shadows soft without console
        // noise on every shadow-map update.
        gl.shadowMap.type = THREE.PCFShadowMap;
        scene.fog = new THREE.Fog("#0b0d0c", diagonal * 3.2, diagonal * 7);
        gl.domElement.addEventListener("webglcontextlost", (event) => {
          event.preventDefault();
          onContextLost();
        });
        setReady(true);
      }}
    >
      <CameraRig width={width} height={height} depth={depth} />
      <Lighting width={width} height={height} depth={depth} />
      <Workbench width={width} depth={depth} />

      <primitive object={engine.group} />

      {ready ? <Interaction engine={engine} uniforms={uniforms} cutRunner={cutRunner} /> : null}
      <FixingMarkers
        engine={engine}
        selectedId={selectedFixingId}
        visible={tool === "fixings"}
        revision={revision}
      />
      <MesherPump engine={engine} />
    </Canvas>
  );
}
